import React, { useState, useCallback, useEffect, useMemo } from 'react';
import './img-spc-dealers-model-common.scss';
import { FMButton, FMDropdown } from '@own/fds-react';
import { Option } from '@own/fds-react/dist/types/fm-dropdown.types';
import ChooseYourServiceVN from '../img-spc-vehicle-service-vn/img-spc-vehicle-service-vn';
import ChooseYourServiceTH from '../img-spc-vehicle-service-th/img-spc-vehicle-service-th';
import ChooseYourServicePH from '../img-spc-vehicle-service-ph/img-spc-vehicle-service-ph';
import {
    ImgSpcDealerModelDetailsApiDataTHPHVN,
    ImgSpcResultsForDerivativeApiDataData,
    ResultsForDerivativeParams,
} from '../../../../models/serice-price-calculator-model';
import { ComponentType } from '../img-service-price-calculator';
import {
    API_RESPONSE,
    ApiResponseStatus,
} from '../../../../contexts/ford-rides-context/ford-rides-context';
import {
    ImgSpcFieldLabelsContentFragment,
    ImgSpcHeadingContentFragment,
    ImgSpcIolmContentFragment,
    ImgSpcMechanicalPartsContentFragment,
    ImgSpcPomContentFragment,
    ImgSpcResultingContentFragment,
} from '../../../../models/img-service-price-calculator/img-service-price-calculator';
import { ImgMaintenanceServiceCost } from '../img-spc-service-maintenance-cost/img-spc-maintenance-service-cost';
import { useAnalytics } from '../../../../hooks/use-analytics';

interface Props {
    setView: (data: ComponentType) => void;
    updateSelectedDetails?: (
        details: Partial<ImgMaintenanceServiceCost>
    ) => void;
    imgSpcDerivativeData: ImgSpcResultsForDerivativeApiDataData | null;
    imgSpcDealerModelData: ImgSpcDealerModelDetailsApiDataTHPHVN;
    fieldsLabel?: ImgSpcFieldLabelsContentFragment;
    headersLabel?: ImgSpcHeadingContentFragment;
    pomLabel?: ImgSpcPomContentFragment;
    resultsLabel?: ImgSpcResultingContentFragment;
    iolmLabel?: ImgSpcIolmContentFragment;
    mechanicalpartsLabel?: ImgSpcMechanicalPartsContentFragment;
    fetchImgSpcDataForDerivative: (
        resultsForDerivativeParams: ResultsForDerivativeParams
    ) => Promise<void>;
    resultsForDerivativeApiStatus: ApiResponseStatus;
    currentLanguageRegionCode: string;
    current3LetterCountryCode: string;
    pomChecked: boolean;
    onPomCheckedChange: (checked: boolean) => void;
    iolmChecked: boolean;
    onIolmCheckedChange: (checked: boolean) => void;
    freeLaborCouponChecked: boolean;
    onFreeLaborCouponChange: (checked: boolean) => void;
    timingBeltChecked: boolean;
    onTimingBeltChange: (checked: boolean) => void;
    mechanicalPartsChecked: boolean;
    onMechanicalPartsChange: (checked: boolean) => void;
    onSelectedMechanicalPartNumbersChange: (partNumbers: string[]) => void;
    onInspectionDiscountApplicableChange: (applicable: boolean) => void;
}

const ImgSPCDealersModelCommon = ({
    setView,
    updateSelectedDetails,
    fieldsLabel,
    headersLabel,
    pomLabel,
    iolmLabel,
    resultsLabel,
    mechanicalpartsLabel,
    imgSpcDealerModelData,
    imgSpcDerivativeData,
    fetchImgSpcDataForDerivative,
    resultsForDerivativeApiStatus,
    currentLanguageRegionCode,
    current3LetterCountryCode,
    pomChecked,
    onPomCheckedChange,
    iolmChecked,
    onIolmCheckedChange,
    freeLaborCouponChecked,
    onFreeLaborCouponChange,
    timingBeltChecked,
    onTimingBeltChange,
    mechanicalPartsChecked,
    onMechanicalPartsChange,
    onSelectedMechanicalPartNumbersChange,
    onInspectionDiscountApplicableChange,
}: Props) => {
    const [state, setState] = useState({
        province: '',
        dealerCode: '',
        dealerName: '',
        nameplateName: '',
        engineSize: '',
        transmission: '',
        derivativeName: '',
        serviceInterval: '',
    });
    const [fireEvents] = useAnalytics();
    const isThaiLanguage = useMemo(() => {
        return currentLanguageRegionCode.toUpperCase() === 'TH-TH';
    }, [currentLanguageRegionCode]);

    const handlePomCheckedChange = useCallback(
        (checked: boolean) => {
            onPomCheckedChange(checked);
        },
        [onPomCheckedChange]
    );

    const handleIolmCheckedChange = useCallback(
        (checked: boolean) => {
            onIolmCheckedChange(checked);
        },
        [onIolmCheckedChange]
    );

    const handleFreeLaborCouponCheckedChange = useCallback(
        (checked: boolean) => {
            onFreeLaborCouponChange(checked);
        },
        [onFreeLaborCouponChange]
    );

    const handleTimingBeltCheckedChange = useCallback(
        (checked: boolean) => {
            onTimingBeltChange(checked);
        },
        [onTimingBeltChange]
    );

    const handleMechanicalPartSelection = useCallback(
        (isSelected: boolean) => {
            onMechanicalPartsChange(isSelected);
        },
        [onMechanicalPartsChange]
    );

    const handleMechanicalPartNumbersChange = useCallback(
        (partNumbers: string[]) => {
            onSelectedMechanicalPartNumbersChange(partNumbers);
        },
        [onSelectedMechanicalPartNumbersChange]
    );

    const handleDropdownChange = (
        event: React.ChangeEvent<HTMLSelectElement> | { target: { value: '' } }
    ) => {
        const updateStateForField = (
            name: string,
            value: string,
            newState: typeof state
        ) => {
            newState[name as keyof typeof newState] = value;

            switch (name) {
                case 'dealerCode':
                    if (value) {
                        const selectElement = event.target as HTMLSelectElement;
                        const selectedOption =
                            selectElement.options[selectElement.selectedIndex];
                        newState.dealerName = selectedOption
                            ? selectedOption.text
                            : '';
                    }
                    break;
                case 'province':
                    newState.dealerCode = '';
                    newState.dealerName = '';
                    break;
                case 'nameplateName':
                    newState.engineSize = '';
                    newState.transmission = '';
                    newState.derivativeName = '';
                    newState.serviceInterval = '';
                    break;
                case 'engineSize':
                    newState.transmission = '';
                    newState.derivativeName = '';
                    newState.serviceInterval = '';
                    break;
                case 'transmission':
                    newState.derivativeName = '';
                    newState.serviceInterval = '';
                    break;
                case 'derivativeName':
                    newState.serviceInterval = '';
                    resetDependentFields();
                    break;
            }
        };

        const resetDependentFields = () => {
            onPomCheckedChange(false);
            onIolmCheckedChange(true);
            onFreeLaborCouponChange(false);
            onTimingBeltChange(false);
            onMechanicalPartsChange(false);
            onSelectedMechanicalPartNumbersChange([]);
        };

        setState(prevState => {
            const newState = { ...prevState };

            if ('name' in event.target) {
                const { name, value } = event.target;
                updateStateForField(name, value, newState);
            } else if (event.target.value === '') {
                newState.serviceInterval = '';
            }
            return newState;
        });
    };

    const provinceOptions = useMemo(() => {
        if (!imgSpcDealerModelData) return [];
        return imgSpcDealerModelData.dealersInfo.map(province => ({
            value: province.provinceState,
            label: isThaiLanguage
                ? province.provinceStateOL ?? province.provinceState
                : province.provinceState,
        }));
    }, [imgSpcDealerModelData, isThaiLanguage]);

    const dealerOptions = useMemo(() => {
        if (!imgSpcDealerModelData) return [];
        const selectedProvince = imgSpcDealerModelData.dealersInfo.find(
            province => province.provinceState === state.province
        );
        if (!selectedProvince) return [];
        return selectedProvince.dealers.map(dealer => ({
            value: dealer.dealerCode,
            label: isThaiLanguage
                ? dealer.dealerNameOL ?? dealer.dealerName
                : dealer.dealerName,
        }));
    }, [imgSpcDealerModelData, state.province, isThaiLanguage]);

    const nameplateOptions = useMemo(() => {
        if (!imgSpcDealerModelData) return [];
        return imgSpcDealerModelData.modelsInfo.map(model => ({
            value: model.nameplateName,
            label: model.nameplateName,
        }));
    }, [imgSpcDealerModelData]);

    const engineSizeOptions = useMemo(() => {
        if (!imgSpcDealerModelData) return [];
        const selectedNameplate = imgSpcDealerModelData.modelsInfo.find(
            model => model.nameplateName === state.nameplateName
        );
        if (!selectedNameplate) return [];
        const uniqueEngineSizes = Array.from(
            new Set(selectedNameplate.models.map(m => m.engineSize))
        );
        return uniqueEngineSizes.map(size => ({
            value: size,
            label: size,
        }));
    }, [imgSpcDealerModelData, state.nameplateName]);

    const transmissionOptions = useMemo(() => {
        if (!imgSpcDealerModelData) return [];
        const selectedNameplate = imgSpcDealerModelData.modelsInfo.find(
            model => model.nameplateName === state.nameplateName
        );
        if (!selectedNameplate) return [];
        const modelsFilteredByEngine = selectedNameplate.models.filter(
            m => m.engineSize === state.engineSize
        );
        const uniqueTransmissions = Array.from(
            new Set(modelsFilteredByEngine.map(m => m.transmission))
        );
        return uniqueTransmissions.map(trans => ({
            value: trans,
            label: trans,
        }));
    }, [imgSpcDealerModelData, state.nameplateName, state.engineSize]);

    const variantNameOptions = useMemo(() => {
        if (!imgSpcDealerModelData) return [];
        const selectedNameplate = imgSpcDealerModelData.modelsInfo.find(
            model => model.nameplateName === state.nameplateName
        );
        if (!selectedNameplate) return [];
        const filteredModels = selectedNameplate.models.filter(
            model =>
                model.engineSize === state.engineSize &&
                model.transmission === state.transmission
        );
        return filteredModels.map(model => ({
            value: model.variantName,
            label: model.variantName,
        }));
    }, [
        imgSpcDealerModelData,
        state.nameplateName,
        state.engineSize,
        state.transmission,
    ]);

    const serviceIntervalOptions = useMemo(() => {
        if (!imgSpcDealerModelData) return [];
        const selectedNameplate = imgSpcDealerModelData.modelsInfo.find(
            model => model.nameplateName === state.nameplateName
        );
        if (!selectedNameplate) return [];
        const filteredModels = selectedNameplate.models.filter(
            model =>
                model.engineSize === state.engineSize &&
                model.transmission === state.transmission
        );
        const variantModel = filteredModels.find(
            model => model.variantName === state.derivativeName
        );
        if (!variantModel) return [];

        const formatServiceInterval = (text: string) => {
            return text.replace(
                /\b(\d{4,})(?=\s*(?:Kms?|Miles?|Km|Mi|\s|$))/gi,
                match => {
                    return parseInt(match).toLocaleString('en-US');
                }
            );
        };

        return variantModel.serviceIntervals.map(interval => ({
            value: interval.yearsFromPurchaseAndDistance,
            label: formatServiceInterval(interval.yearsFromPurchaseAndDistance),
        }));
    }, [
        imgSpcDealerModelData,
        state.nameplateName,
        state.engineSize,
        state.transmission,
        state.derivativeName,
    ]);

    useEffect(() => {
        if (dealerOptions.length === 1 && !state.dealerCode) {
            setState(prev => ({
                ...prev,
                dealerCode: dealerOptions[0].value,
                dealerName: dealerOptions[0].label,
            }));
        }
    }, [dealerOptions, state.dealerCode]);

    useEffect(() => {
        if (engineSizeOptions.length === 1 && !state.engineSize) {
            setState(prev => ({
                ...prev,
                engineSize: engineSizeOptions[0].value,
            }));
        }
    }, [engineSizeOptions, state.engineSize]);

    useEffect(() => {
        if (transmissionOptions.length === 1 && !state.transmission) {
            setState(prev => ({
                ...prev,
                transmission: transmissionOptions[0].value,
            }));
        }
    }, [transmissionOptions, state.transmission]);

    useEffect(() => {
        if (variantNameOptions.length === 1 && !state.derivativeName) {
            setState(prev => ({
                ...prev,
                derivativeName: variantNameOptions[0].value,
            }));
        }
    }, [variantNameOptions, state.derivativeName]);

    useEffect(() => {
        if (serviceIntervalOptions.length === 1 && !state.serviceInterval) {
            setState(prev => ({
                ...prev,
                serviceInterval: serviceIntervalOptions[0].value,
            }));
        }
    }, [serviceIntervalOptions, state.serviceInterval]);

    const areAllMandatoryFieldsSelected = useCallback(() => {
        return (
            state.province &&
            state.dealerCode &&
            state.dealerName &&
            state.nameplateName &&
            state.engineSize &&
            state.transmission &&
            state.derivativeName
        );
    }, [state]);

    useEffect(() => {
        if (areAllMandatoryFieldsSelected()) {
            fetchImgSpcDataForDerivative({
                countryCode: current3LetterCountryCode.toUpperCase(),
                isServiceApplicable: '1',
                dealerCode: state.dealerCode,
                engineSize: state.engineSize,
                derivativeName: state.derivativeName,
            });
        }
    }, [
        state.derivativeName,
        state.engineSize,
        state.dealerCode,
        areAllMandatoryFieldsSelected,
        fetchImgSpcDataForDerivative,
        current3LetterCountryCode,
    ]);

    const handleSubmit = useCallback(
        async (e: React.FormEvent<HTMLFormElement>) => {
            e.preventDefault();

            if (!areAllMandatoryFieldsSelected()) {
                return;
            }

            await fetchImgSpcDataForDerivative({
                countryCode: current3LetterCountryCode.toUpperCase(),
                isServiceApplicable: '1',
                dealerCode: state.dealerCode,
                engineSize: state.engineSize,
                derivativeName: state.derivativeName,
                serviceInterval: state.serviceInterval || undefined,
                ...(iolmChecked ? { nextGenOilPartNum: 'NEXTGENOIL' } : {}),
            });

            if (updateSelectedDetails) {
                updateSelectedDetails({
                    dealerName: state.dealerName,
                    derivativeName: state.derivativeName,
                    serviceInterval: state.serviceInterval,
                });
            }
            setView(ComponentType.ImgSpcServiceCost);
        },
        [
            areAllMandatoryFieldsSelected,
            current3LetterCountryCode,
            state,
            fetchImgSpcDataForDerivative,
            setView,
            updateSelectedDetails,
            iolmChecked,
            onPomCheckedChange,
            onIolmCheckedChange,
            onFreeLaborCouponChange,
            onTimingBeltChange,
            onMechanicalPartsChange,
            onSelectedMechanicalPartNumbersChange,
        ]
    );

    const renderServiceComponent = () => {
        const countryCode = currentLanguageRegionCode
            .split('-')[1]
            ?.toLowerCase();
        const allMandatorySelected = areAllMandatoryFieldsSelected();

        if (!allMandatorySelected) return null;

        const serviceIntervalProps = {
            service: serviceIntervalOptions,
            intervalDropdown: handleDropdownChange,
            serviceInterval: state.serviceInterval,
            provinceCity: state.province,
            dealerCode: state.dealerCode,
            dealerName: state.dealerName,
            nameplate: state.nameplateName,
            engineSize: state.engineSize,
            transmission: state.transmission,
            derivativeName: state.derivativeName,
            setView,
            updateSelectedDetails,
            headersLabel,
            fieldsLabel,
        };

        switch (countryCode) {
            case 'vn':
                return (
                    <ChooseYourServiceVN
                        {...serviceIntervalProps}
                        imgSpcDerivativeData={imgSpcDerivativeData}
                        iolmLabel={iolmLabel}
                        pomLabel={pomLabel}
                        pomChecked={pomChecked}
                        onPomCheckedChange={handlePomCheckedChange}
                        iolmChecked={iolmChecked}
                        onIolmCheckedChange={handleIolmCheckedChange}
                    />
                );
            case 'th':
                return (
                    <ChooseYourServiceTH
                        {...serviceIntervalProps}
                        pomChecked={pomChecked}
                        onPomCheckedChange={handlePomCheckedChange}
                        iolmChecked={iolmChecked}
                        onIolmCheckedChange={handleIolmCheckedChange}
                        freeLaborCouponChecked={freeLaborCouponChecked}
                        onFreeLaborCouponCheckedChange={
                            handleFreeLaborCouponCheckedChange
                        }
                        timingBeltChecked={timingBeltChecked}
                        onTimingBeltCheckedChange={
                            handleTimingBeltCheckedChange
                        }
                        mechanicalPartsChecked={mechanicalPartsChecked}
                        onMechanicalPartsCheckedChange={
                            handleMechanicalPartSelection
                        }
                        onMechanicalPartNumbersChange={
                            handleMechanicalPartNumbersChange
                        }
                        pomLabel={pomLabel}
                        mechanicalpartsLabel={mechanicalpartsLabel}
                        imgSpcDerivativeData={imgSpcDerivativeData}
                        iolmLabel={iolmLabel}
                    />
                );
            case 'ph':
                return (
                    <ChooseYourServicePH
                        {...serviceIntervalProps}
                        imgSpcDealerModelData={imgSpcDealerModelData}
                        imgSpcDerivativeData={imgSpcDerivativeData}
                        pomChecked={pomChecked}
                        derivativeName={state.derivativeName}
                        onPomCheckedChange={handlePomCheckedChange}
                        iolmChecked={iolmChecked}
                        onIolmCheckedChange={handleIolmCheckedChange}
                        freeLaborCouponChecked={freeLaborCouponChecked}
                        onFreeLaborCouponCheckedChange={
                            handleFreeLaborCouponCheckedChange
                        }
                        onInspectionDiscountApplicableChange={
                            onInspectionDiscountApplicableChange
                        }
                        pomLabel={pomLabel}
                        iolmLabel={iolmLabel}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <div className="spc-main-container">
            <form className="spc-container" onSubmit={handleSubmit}>
                <div className="spc-dropdown-container">
                    <FMDropdown
                        name="province"
                        customClass="filter-dropdown"
                        value={state.province}
                        header={fieldsLabel?.provinceStateFieldLabel}
                        options={provinceOptions}
                        ariaLabel={
                            fieldsLabel?.provinceStatePlaceHolderText ??
                            'Please Select Province'
                        }
                        onChange={handleDropdownChange}
                        renderOwnOption={(options?: string[] | Option[]) => {
                            const opts = options as Option[];
                            return (
                                <>
                                    <option value="">
                                        {
                                            fieldsLabel?.provinceStatePlaceHolderText
                                        }
                                    </option>
                                    {opts.map(opt => (
                                        <option
                                            key={opt.value}
                                            value={opt.value}
                                        >
                                            {opt.label}
                                        </option>
                                    ))}
                                </>
                            );
                        }}
                    />
                    <FMDropdown
                        name="dealerCode"
                        customClass="filter-dropdown"
                        value={state.dealerCode}
                        header={fieldsLabel?.preferredDealerLabel}
                        options={dealerOptions}
                        ariaLabel={
                            fieldsLabel?.preferredDealerPlaceHolderText ??
                            'Please Select Dealer'
                        }
                        onChange={handleDropdownChange}
                        disabled={dealerOptions.length === 0}
                        renderOwnOption={(options?: string[] | Option[]) => {
                            const opts = options as Option[];
                            return (
                                <>
                                    <option value="">
                                        {
                                            fieldsLabel?.preferredDealerPlaceHolderText
                                        }
                                    </option>
                                    {opts.map(opt => (
                                        <option
                                            key={opt.value}
                                            value={opt.value}
                                        >
                                            {opt.label}
                                        </option>
                                    ))}
                                </>
                            );
                        }}
                    />
                    <FMDropdown
                        name="nameplateName"
                        customClass="filter-dropdown"
                        value={state.nameplateName}
                        header={fieldsLabel?.selectYourVehicleLabel}
                        options={nameplateOptions}
                        ariaLabel={
                            fieldsLabel?.selectYourVehiclePlaceHolderText ??
                            'Please Select Vehicle'
                        }
                        onChange={handleDropdownChange}
                        disabled={nameplateOptions.length === 0}
                        renderOwnOption={(options?: string[] | Option[]) => {
                            const opts = options as Option[];
                            return (
                                <>
                                    <option value="">
                                        {
                                            fieldsLabel?.selectYourVehiclePlaceHolderText
                                        }
                                    </option>
                                    {opts.map(opt => (
                                        <option
                                            key={opt.value}
                                            value={opt.value}
                                        >
                                            {opt.label}
                                        </option>
                                    ))}
                                </>
                            );
                        }}
                    />
                    <FMDropdown
                        name="engineSize"
                        value={state.engineSize}
                        customClass="filter-dropdown"
                        header={fieldsLabel?.engineSizeLabel}
                        options={engineSizeOptions}
                        ariaLabel={
                            fieldsLabel?.engineSizePlaceHolderText ??
                            'Please Select Engine Size'
                        }
                        onChange={handleDropdownChange}
                        disabled={engineSizeOptions.length === 0}
                        renderOwnOption={(options?: string[] | Option[]) => {
                            const opts = options as Option[];
                            return (
                                <>
                                    <option value="">
                                        {fieldsLabel?.engineSizePlaceHolderText}
                                    </option>
                                    {opts.map(opt => (
                                        <option
                                            key={opt.value}
                                            value={opt.value}
                                        >
                                            {opt.label}
                                        </option>
                                    ))}
                                </>
                            );
                        }}
                    />
                    <FMDropdown
                        name="transmission"
                        customClass="filter-dropdown"
                        header={fieldsLabel?.transmissionFieldLabel}
                        value={state.transmission}
                        options={transmissionOptions}
                        ariaLabel={
                            fieldsLabel?.transmissionPlaceHolderText ??
                            'Please Select Transmission'
                        }
                        onChange={handleDropdownChange}
                        disabled={transmissionOptions.length === 0}
                        renderOwnOption={(options?: string[] | Option[]) => {
                            const opts = options as Option[];
                            return (
                                <>
                                    <option value="">
                                        {
                                            fieldsLabel?.transmissionPlaceHolderText
                                        }
                                    </option>
                                    {opts.map(opt => (
                                        <option
                                            key={opt.value}
                                            value={opt.value}
                                        >
                                            {opt.label}
                                        </option>
                                    ))}
                                </>
                            );
                        }}
                    />
                    <FMDropdown
                        name="derivativeName"
                        customClass="filter-dropdown"
                        header={fieldsLabel?.variantFieldLabel}
                        value={state.derivativeName}
                        options={variantNameOptions}
                        ariaLabel={
                            fieldsLabel?.variantPlaceHolderText ??
                            'Please Select Your Variant'
                        }
                        onChange={handleDropdownChange}
                        disabled={variantNameOptions.length === 0}
                        renderOwnOption={(options?: string[] | Option[]) => {
                            const opts = options as Option[];
                            return (
                                <>
                                    <option value="">
                                        {fieldsLabel?.variantPlaceHolderText}
                                    </option>
                                    {opts.map(opt => (
                                        <option
                                            key={opt.value}
                                            value={opt.value}
                                        >
                                            {opt.label}
                                        </option>
                                    ))}
                                </>
                            );
                        }}
                    />
                </div>
                {renderServiceComponent()}

                {pomChecked ? (
                    <div className="group-buttons">
                        <FMButton
                            htmlButtonType="button"
                            label={resultsLabel?.bookAServiceCTALabel}
                            type="primary"
                            className="img-spc-service-button"
                            onClick={() => {
                                fireEvents(
                                    ['spc-navigation-cta'],
                                    undefined,
                                    {
                                        ctaType:
                                            resultsLabel?.bookAServiceCTALabel,
                                    },
                                    false
                                );
                                if (resultsLabel?.bookAServiceCTAPath) {
                                    window.open(
                                        resultsLabel.bookAServiceCTAPath,
                                        '_blank'
                                    );
                                }
                            }}
                        />
                        <FMButton
                            htmlButtonType="button"
                            label={resultsLabel?.findADealerCTALabel}
                            type="secondary"
                            className="img-spc-dealer-button"
                            onClick={() => {
                                fireEvents(
                                    ['spc-navigation-cta'],
                                    undefined,
                                    {
                                        ctaType:
                                            resultsLabel?.findADealerCTALabel,
                                    },
                                    false
                                );
                                if (resultsLabel?.findADealerPath) {
                                    window.open(
                                        resultsLabel.findADealerPath,
                                        '_blank'
                                    );
                                }
                            }}
                        />
                    </div>
                ) : (
                    <></>
                )}
                {resultsForDerivativeApiStatus === API_RESPONSE.FAILURE && (
                    <p className="error-message">
                        {headersLabel?.resultsForDerivativeApiErrorMessage}
                    </p>
                )}
            </form>
        </div>
    );
};

export default ImgSPCDealersModelCommon;
